import { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, Users, DollarSign, ArrowDownCircle, ArrowUpCircle,
  TrendingUp, Shield, Activity, RefreshCw
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import api from '../../services/api';

const PERIODS = [
  { key: '7d', label: '7 Days' },
  { key: '30d', label: '30 Days' },
  { key: '90d', label: '90 Days' },
  { key: 'all', label: 'All Time' },
];

const AdminAnalyticsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, analyticsRes] = await Promise.all([
        api.get('/admin/dashboard/stats', { headers }),
        api.post('/admin/analytics', { metric: 'overview', period }, { headers }),
      ]);

      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Analytics fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 60000);
    return () => clearInterval(iv);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent" />
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const timeSeries = analytics?.time_series || [];

  const statCards = [
    { icon: Users, label: 'Total Users', value: stats?.total_users || 0, sub: `+${stats?.new_users_today || 0} today`, color: 'bg-blue-600' },
    { icon: Activity, label: 'Active Users', value: stats?.active_users || 0, color: 'bg-green-600' },
    { icon: DollarSign, label: 'Platform Balance', value: `$${(stats?.total_balance || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, color: 'bg-purple-600' },
    { icon: ArrowDownCircle, label: 'Pending Deposits', value: stats?.pending_deposits || 0, color: 'bg-emerald-600' },
    { icon: ArrowUpCircle, label: 'Pending Withdrawals', value: stats?.pending_withdrawals || 0, color: 'bg-red-600' },
    { icon: TrendingUp, label: 'Active Trades', value: (stats?.active_binary_trades || 0) + (stats?.active_arbitrage_investments || 0), color: 'bg-cyan-600' },
    { icon: Shield, label: 'Pending KYC', value: stats?.kyc_pending || 0, color: 'bg-yellow-600' },
    { icon: BarChart3, label: 'Win Rate', value: `${summary.win_rate || 0}%`, color: 'bg-indigo-600' },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl text-sm">
        <p className="text-gray-300 font-semibold mb-1">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? `$${entry.value.toLocaleString()}` : entry.value}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Analytics Dashboard</h1>
          <p className="text-gray-400 text-sm mt-1">Platform performance and insights</p>
        </div>
        <div className="flex items-center gap-2">
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => { setLoading(true); setPeriod(p.key); }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                period === p.key
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
              data-testid={`period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
          <button onClick={() => { setLoading(true); fetchData(); }} className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stat-cards">
        {statCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <div key={i} className="bg-gray-900 border border-gray-800 rounded-xl p-4" data-testid={`stat-${card.label.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`${card.color} p-2 rounded-lg`}>
                  <Icon className="h-4 w-4 text-white" />
                </div>
                <span className="text-gray-400 text-xs font-medium">{card.label}</span>
              </div>
              <p className="text-xl font-bold text-white">{card.value}</p>
              {card.sub && <p className="text-xs text-green-400 mt-1">{card.sub}</p>}
            </div>
          );
        })}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h3 className="text-gray-400 text-sm mb-3">Financial Flow ({period})</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">Total Deposits</span>
              <span className="text-green-400 font-semibold">${(summary.total_deposits || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">Total Withdrawals</span>
              <span className="text-red-400 font-semibold">${(summary.total_withdrawals || 0).toLocaleString()}</span>
            </div>
            <div className="border-t border-gray-800 pt-2 flex justify-between">
              <span className="text-gray-400 text-sm font-medium">Net Flow</span>
              <span className={`font-bold ${(summary.net_flow || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                ${(summary.net_flow || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h3 className="text-gray-400 text-sm mb-3">Trading Activity ({period})</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">Trade Volume</span>
              <span className="text-cyan-400 font-semibold">${(summary.total_trade_volume || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">Trade Count</span>
              <span className="text-white font-semibold">{summary.trade_count || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">Win Rate</span>
              <span className="text-yellow-400 font-semibold">{summary.win_rate || 0}%</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h3 className="text-gray-400 text-sm mb-3">AI Arbitrage ({period})</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">Total Invested</span>
              <span className="text-purple-400 font-semibold">${(summary.arbitrage_volume || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">Active Investments</span>
              <span className="text-white font-semibold">{summary.arbitrage_active || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">User Growth</span>
              <span className="text-blue-400 font-semibold">+{summary.new_users || 0} users</span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      {timeSeries.length > 0 && (
        <>
          {/* User Growth Chart */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid="chart-user-growth">
            <h3 className="text-white font-semibold mb-4">User Growth</h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={timeSeries}>
                <defs>
                  <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 11 }} />
                <YAxis stroke="#6b7280" tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="new_users" name="New Users" stroke="#8b5cf6" fill="url(#gradUsers)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Financial Flow Chart */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid="chart-financial-flow">
            <h3 className="text-white font-semibold mb-4">Financial Flow</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={timeSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 11 }} />
                <YAxis stroke="#6b7280" tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ color: '#9ca3af', fontSize: 12 }} />
                <Bar dataKey="deposit_volume" name="Deposits" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="withdrawal_volume" name="Withdrawals" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Trade Volume Chart */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid="chart-trade-volume">
            <h3 className="text-white font-semibold mb-4">Trading Volume</h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={timeSeries}>
                <defs>
                  <linearGradient id="gradTrade" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 11 }} />
                <YAxis stroke="#6b7280" tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="trade_volume" name="Trade Volume" stroke="#06b6d4" fill="url(#gradTrade)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {timeSeries.length === 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-12 text-center">
          <BarChart3 className="h-12 w-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-lg">No data available for the selected period</p>
          <p className="text-gray-500 text-sm mt-1">Try selecting a different time range</p>
        </div>
      )}
    </div>
  );
};

export default AdminAnalyticsDashboard;
